import { Link, useLocation } from "wouter";
import { useUser } from "@/hooks/use-user";
import { Button } from "@/components/ui/button";
import { useEffect, useState } from "react";
import {
  LayoutDashboard,
  Users,
  FolderKanban,
  UserCheck,
  LogOut,
  BarChart,
  Building,
  User2Icon,
  Menu,
  LinkedinIcon
} from "lucide-react";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";

export default function Layout({ children }: { children: React.ReactNode }) {
  const [location] = useLocation();
  const { user, isLoading, logout } = useUser();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    if (!isLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, isLoading]);

  // Show loading state
  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div>Loading...</div>
      </div>
    );
  }

  // Redirect if no user
  if (!user) {
    return null;
  }

  const navigation = [
    { name: "Dashboard", href: "/", icon: LayoutDashboard },
    { name: "Agencies", href: "/agencies", icon: Building, },
    { name: "HR", href: "/hr", icon: User2Icon },
    { name: "Vendors", href: "/vendors", icon: Users },
    { name: "Roles", href: "/roles", icon: FolderKanban },
    { name: "Candidates", href: "/candidates", icon: UserCheck },
    { name: "Analytics", href: "/analytics", icon: BarChart },
    {name: "LinkedIn Search", href:"/linkedinsearch",icon:LinkedinIcon},
  
    
    
  ];

  const SidebarContent = () => (
    <div className="flex flex-1 flex-col overflow-y-auto pt-5 pb-4">
      <div className="flex flex-shrink-0 items-center px-4">
        <h1 className="text-2xl font-bold">TalentIQ</h1>
      </div>
      <nav className="mt-5 flex-1 space-y-1 bg-background px-2">
        {navigation.map((item) => {
          const isActive = location === item.href;
          return (
            <Link
              key={item.name}
              href={item.href}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <div
                className={`group flex items-center rounded-md px-2 py-2 text-sm font-medium cursor-pointer ${
                  isActive
                    ? "bg-secondary text-primary"
                    : "text-muted-foreground hover:bg-secondary"
                }`}
              >
                <item.icon
                  className={`mr-3 h-5 w-5 flex-shrink-0 ${
                    isActive ? "text-primary" : "text-muted-foreground"
                  }`}
                />
                {item.name}
              </div>
            </Link>
          );
        })}
      </nav>
      <div className="flex flex-shrink-0 border-t p-4">
        <div className="flex flex-1 items-center">
          <div className="ml-3">
            <p className="text-sm font-medium text-foreground">
              {user.email}
            </p>
            <p className="text-xs text-muted-foreground">User</p>
          </div>
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => logout()}
          className="ml-auto"
        >
          <LogOut className="h-5 w-5" />
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <div className="flex">
        {/* Desktop Sidebar */}
        <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
          <div className="flex flex-1 flex-col border-r">
            <SidebarContent />
          </div>
        </div>

        {/* Mobile Sidebar */}
        <Sheet open={isMobileMenuOpen} onOpenChange={setIsMobileMenuOpen}>
          <SheetTrigger asChild className="lg:hidden">
            <Button variant="ghost" size="icon" className="fixed top-6 left-6 z-50">
              <Menu className="h-6 w-6" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SidebarContent />
          </SheetContent>
        </Sheet>

        {/* Main content */}
        <div className="flex flex-1 flex-col lg:pl-64">
          <main className="flex-1">
            <div className="py-6 mt-12 lg:mt-0">
              <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                {children}
              </div>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}